import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useToggleCommentLike } from "@/hooks/use-project-comments";
import { summarizeReactions, type ReactionEmoji, type ReactionRow, type ReactionSummary } from "@/lib/chat-room/reactions";
import type { Channel } from "@/lib/chat-room/channels";
import type { ChannelMessage } from "@/lib/chat-room/messages";

interface RawReaction {
  chat_message_id: string | null;
  project_comment_id: string | null;
  user_id: string;
  emoji: string;
}

export interface ToggleReactionArgs {
  messageId: string;
  emoji: ReactionEmoji;
  mine: boolean;
}

/**
 * Reaksjoner på meldingene i én kanal i Chat-rommet (VIB-3025).
 * `message_reactions` for 🎉/👀 (og 👍 i rom), `project_comment_likes` for 👍
 * på prosjektoppdateringer, så tallet er likt på prosjektkortet.
 *
 * Brukeren er den ekte sesjonen: RLS krever `user_id = current_user_id()`.
 */
export function useMessageReactions(channel: Channel | null, messages: ChannelMessage[], selfId: string | null) {
  const qc = useQueryClient();
  const isProject = channel?.kind === "project";
  const column = isProject ? "project_comment_id" : "chat_message_id";
  const messageIds = useMemo(() => messages.filter((m) => m.kind === "user").map((m) => m.id), [messages]);
  const toggleLike = useToggleCommentLike();

  const query = useQuery({
    queryKey: ["message-reactions", channel?.key ?? null, messageIds],
    enabled: !!channel && messageIds.length > 0,
    queryFn: async (): Promise<ReactionRow[]> => {
      const { data, error } = await supabase
        .from("message_reactions")
        .select("chat_message_id, project_comment_id, user_id, emoji")
        .in(column, messageIds)
        .is("deleted_at", null);
      if (error) throw error;
      return ((data ?? []) as RawReaction[]).map((r) => ({
        message_id: (r.chat_message_id ?? r.project_comment_id) as string,
        user_id: r.user_id,
        emoji: r.emoji,
      }));
    },
  });

  const summaries = useMemo(() => {
    const likes = new Map(messages.filter((m) => m.likedByUserIds.length > 0).map((m) => [m.id, m.likedByUserIds]));
    return summarizeReactions(query.data ?? [], likes, selfId);
  }, [query.data, messages, selfId]);

  const toggle = useMutation({
    mutationFn: async ({ messageId, emoji, mine }: ToggleReactionArgs) => {
      if (!selfId || !channel) return;
      if (isProject && emoji === "👍") {
        await toggleLike.mutateAsync({
          comment_id: messageId,
          user_id: selfId,
          project_id: channel.id,
          currently_liked: mine,
        });
        return;
      }
      if (mine) {
        const { error } = await supabase
          .from("message_reactions")
          .update({ deleted_at: new Date().toISOString() })
          .eq(column, messageId)
          .eq("user_id", selfId)
          .eq("emoji", emoji)
          .is("deleted_at", null);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("message_reactions")
          .insert({ [column]: messageId, user_id: selfId, emoji });
        if (error) throw error;
      }
    },
    onSettled: async () => {
      await invalidateFor(qc, "message_reactions", { channelKey: channel?.key });
    },
    onError: () => {
      toast.error("Kunne ikke lagre reaksjonen");
    },
  });

  return {
    summaryFor: (messageId: string): ReactionSummary[] => summaries.get(messageId) ?? [],
    toggle: (args: ToggleReactionArgs) => toggle.mutate(args),
    isPending: toggle.isPending,
  };
}
